export const SUPPORTED_FORM_LANGUAGES = ['en', 'es', 'it', 'fr', 'de'] as const;

export type SupportedFormLanguage = (typeof SUPPORTED_FORM_LANGUAGES)[number];

const LANGUAGE_SEPARATOR_PATTERN = /[_-]/;

interface FooterCopy {
  beforeTerms: string;
  termsLabel: string;
  betweenTermsAndPrivacy: string;
  privacyLabel: string;
  afterPrivacy: string;
}

interface DefaultQualificationCopy {
  questionLabel: string;
  optionLabels: readonly string[];
}

interface FormLocalizationCopy {
  footer: FooterCopy;
  defaultQualification: DefaultQualificationCopy;
}

const DEFAULT_FORM_LANGUAGE: SupportedFormLanguage = 'en';

export const DEFAULT_QUALIFICATION_PAYLOAD_OPTIONS = [
  'Just curious, not actively purchasing software',
  "I'm beginning my research and gathering requirements",
  "I'm comparing several options",
  "I've narrowed it down to a few top contenders",
  "I've recently made a decision",
] as const;

const DEFAULT_QUALIFICATION_OPTION_LABELS_BY_LANGUAGE: Record<
  SupportedFormLanguage,
  readonly string[]
> = {
  en: DEFAULT_QUALIFICATION_PAYLOAD_OPTIONS,
  es: [
    'Miraba por curiosidad, no tenía intención de comprar software',
    'Estoy empezando la búsqueda y viendo los requisitos',
    'Estoy comparando varias opciones',
    'Ya he reducido la lista hasta incluir solo las mejores opciones',
    'Hace poco que he tomado una decisión al respecto',
  ],
  it: [
    "Cerco per curiosità, senza l'intenzione di acquistare un software",
    'Ho appena iniziato la ricerca e analizzo i requisiti',
    'Sto confrontando diverse opzioni',
    'Ho selezionato alcune delle opzioni che mi interessano di più',
    'Ho appena preso una decisione',
  ],
  fr: [
    'Je regarde par curiosité, je ne veux pas acheter de logiciel de suite',
    'Je commence tout juste à me renseigner',
    'Je compare plusieurs logiciels',
    "J'ai une liste de logiciels qui m'intéressent",
    'Je viens de choisir un logiciel',
  ],
  de: [
    'Ich bin nur neugierig und nicht aktiv beim Softwarekauf',
    'Ich beginne mit der Recherche und stelle Anforderungen zusammen',
    'Ich vergleiche verschiedene Optionen',
    'Ich habe die Auswahl auf einige am besten geeignete Optionen eingegrenzt',
    'Ich habe kürzlich eine Entscheidung getroffen',
  ],
};

const FORM_LOCALIZATION_BY_LANGUAGE: Record<
  SupportedFormLanguage,
  FormLocalizationCopy
> = {
  en: {
    footer: {
      beforeTerms: 'By proceeding, you agree to our ',
      termsLabel: 'Terms Of Use',
      betweenTermsAndPrivacy: ' and ',
      privacyLabel: 'Privacy Policy',
      afterPrivacy: '.',
    },
    defaultQualification: {
      questionLabel:
        'Which best describes where you are in your software search?',
      optionLabels: DEFAULT_QUALIFICATION_OPTION_LABELS_BY_LANGUAGE.en,
    },
  },
  es: {
    footer: {
      beforeTerms: 'Al continuar, aceptas nuestras ',
      termsLabel: 'Condiciones de uso',
      betweenTermsAndPrivacy: ' y la ',
      privacyLabel: 'Política de privacidad',
      afterPrivacy: '.',
    },
    defaultQualification: {
      questionLabel:
        '¿Cuál de estas opciones describe con más precisión el momento de la búsqueda de software en el que te encuentras?',
      optionLabels: DEFAULT_QUALIFICATION_OPTION_LABELS_BY_LANGUAGE.es,
    },
  },
  it: {
    footer: {
      beforeTerms: 'Continuando, accetti i nostri ',
      termsLabel: 'Termini di utilizzo',
      betweenTermsAndPrivacy: " e l'",
      privacyLabel: 'Informativa sulla privacy',
      afterPrivacy: '.',
    },
    defaultQualification: {
      questionLabel:
        'Quale opzione descrive meglio il momento della ricerca di software in cui ti trovi?',
      optionLabels: DEFAULT_QUALIFICATION_OPTION_LABELS_BY_LANGUAGE.it,
    },
  },
  fr: {
    footer: {
      beforeTerms: 'En continuant, vous acceptez nos ',
      termsLabel: "Conditions d'utilisation",
      betweenTermsAndPrivacy: ' et notre ',
      privacyLabel: 'Politique de confidentialité',
      afterPrivacy: '.',
    },
    defaultQualification: {
      questionLabel: 'Où en êtes-vous dans votre recherche de logiciel ?',
      optionLabels: DEFAULT_QUALIFICATION_OPTION_LABELS_BY_LANGUAGE.fr,
    },
  },
  de: {
    footer: {
      beforeTerms: 'Indem du fortfährst, stimmst du unseren ',
      termsLabel: 'Nutzungsbedingungen',
      betweenTermsAndPrivacy: ' und unserer ',
      privacyLabel: 'Datenschutzrichtlinie',
      afterPrivacy: ' zu.',
    },
    defaultQualification: {
      questionLabel:
        'Was beschreibt am besten, wo du aktuell bei der Softwaresuche stehst?',
      optionLabels: DEFAULT_QUALIFICATION_OPTION_LABELS_BY_LANGUAGE.de,
    },
  },
};

const SUPPORTED_FORM_LANGUAGE_SET = new Set<string>(SUPPORTED_FORM_LANGUAGES);

export const normalizeFormLanguage = (
  lang?: string | null
): SupportedFormLanguage => {
  if (!lang) {
    return DEFAULT_FORM_LANGUAGE;
  }

  const normalizedLanguage = lang
    .trim()
    .toLowerCase()
    .split(LANGUAGE_SEPARATOR_PATTERN)[0];

  return SUPPORTED_FORM_LANGUAGE_SET.has(normalizedLanguage)
    ? (normalizedLanguage as SupportedFormLanguage)
    : DEFAULT_FORM_LANGUAGE;
};

export const getLocalizedFooterCopy = (lang?: string | null): FooterCopy =>
  FORM_LOCALIZATION_BY_LANGUAGE[normalizeFormLanguage(lang)].footer;

export const getLocalizedDefaultQualificationCopy = (
  lang?: string | null
): { label: string; options: string[] } => {
  const normalizedLanguage = normalizeFormLanguage(lang);
  const { defaultQualification } =
    FORM_LOCALIZATION_BY_LANGUAGE[normalizedLanguage];

  const options =
    normalizedLanguage === DEFAULT_FORM_LANGUAGE
      ? [...DEFAULT_QUALIFICATION_PAYLOAD_OPTIONS]
      : defaultQualification.optionLabels.map((label, index) => {
          const payloadValue = DEFAULT_QUALIFICATION_PAYLOAD_OPTIONS[index];

          return `${label}|${payloadValue}`;
        });

  return {
    label: defaultQualification.questionLabel,
    options,
  };
};
